import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { updateTripChatItem } from 'tripPlanner/api'
import type {
  TransportConnection,
  TransportOption,
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
  UpdateTripChatItemResponse,
} from 'tripPlanner/api/types'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { normalizeTripListNote } from '../utils/tripListNoteUtils'

interface UpdateTripListNoteParams {
  rowId: string
  itemId: number
  notes?: string
}

interface MutationContext {
  previousItinerary?: TripItineraryData
}

function updateItemNotes<Item extends TripItineraryItem | TripItemWithoutDay>(
  item: Item,
  itemId: number,
  notes: string | undefined,
): Item {
  if (item.id !== itemId || item.notes === notes) return item
  return { ...item, notes }
}

function updateTransportOptionNotes(
  option: TransportOption,
  itemId: number,
  notes: string | undefined,
): TransportOption {
  const nextReturnLeg = option.returnLeg ?
      updateTransportOptionNotes(option.returnLeg, itemId, notes) :
    undefined

  const optionChanged = option.itemId === itemId && option.notes !== notes
  const returnLegChanged = nextReturnLeg !== option.returnLeg

  if (!optionChanged && !returnLegChanged) return option

  return {
    ...option,
    ...(optionChanged ? { notes } : {}),
    ...(nextReturnLeg ? { returnLeg: nextReturnLeg } : {}),
  }
}

function updateTransportConnectionNotes(
  connection: TransportConnection,
  itemId: number,
  notes: string | undefined,
): TransportConnection {
  let changed = false
  const options = connection.options.map((option) => {
    const nextOption = updateTransportOptionNotes(option, itemId, notes)
    if (nextOption !== option) changed = true
    return nextOption
  })

  return changed ? { ...connection, options } : connection
}

export function updateTripItineraryItemNotes(
  itineraryData: TripItineraryData | undefined,
  itemId: number,
  notes: string | undefined,
): TripItineraryData | undefined {
  if (!itineraryData) return itineraryData

  let changed = false
  const stops = itineraryData.stops.map((stop) => {
    let stopChanged = false
    const days = stop.days.map((day) => {
      let dayChanged = false
      const items = day.items.map((item) => {
        const nextItem = updateItemNotes(item, itemId, notes)
        if (nextItem !== item) dayChanged = true
        return nextItem
      })

      if (!dayChanged) return day
      stopChanged = true
      return { ...day, items }
    })

    const itemsWithoutDay = stop.itemsWithoutDay.map((item) => {
      const nextItem = updateItemNotes(item, itemId, notes)
      if (nextItem !== item) stopChanged = true
      return nextItem
    })

    const transports = stop.transports?.map((connection) => {
      const nextConnection = updateTransportConnectionNotes(connection, itemId, notes)
      if (nextConnection !== connection) stopChanged = true
      return nextConnection
    })

    if (!stopChanged) return stop
    changed = true
    return {
      ...stop,
      days,
      itemsWithoutDay,
      ...(transports ? { transports } : {}),
    }
  })

  return changed ? { ...itineraryData, stops } : itineraryData
}

export function useTripListNoteUpdate(chatId: number | undefined, region: string | undefined) {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const [pendingRowId, setPendingRowId] = useState<string | undefined>(undefined)
  const itineraryQueryKey = ChatKeys.itineraryDetail(chatId ?? 0, region)

  const { mutate, isPending: isUpdating } = useMutation<UpdateTripChatItemResponse, unknown, UpdateTripListNoteParams, MutationContext>({
    mutationFn: async(params) => updateTripChatItem(params.itemId, { notes: normalizeTripListNote(params.notes) ?? null }),
    onMutate: async(params) => {
      const notes = normalizeTripListNote(params.notes)
      setPendingRowId(params.rowId)
      await queryClient.cancelQueries({ queryKey: itineraryQueryKey })
      const previousItinerary = queryClient.getQueryData<TripItineraryData>(itineraryQueryKey)
      queryClient.setQueryData<TripItineraryData | undefined>(
        itineraryQueryKey,
        (previous) => updateTripItineraryItemNotes(previous, params.itemId, notes),
      )
      return { previousItinerary }
    },
    onError: (_error, _params, context) => {
      queryClient.setQueryData(itineraryQueryKey, context?.previousItinerary)
      showSnackbar(
        intl.formatMessage({
          id: '3Cz-caVK1AsMF_QAcyZJ',
          defaultMessage: 'Failed to update note. Please try again.',
          description: 'Snackbar error shown when saving an itinerary list note fails in TripChat',
        }),
        'critical',
      )
    },
    onSettled: async() => {
      setPendingRowId(undefined)
      await queryClient.invalidateQueries({ queryKey: itineraryQueryKey })
    },
  })

  const updateNote = useCallback((params: UpdateTripListNoteParams) => {
    if (!chatId) return
    mutate(params)
  }, [chatId, mutate])

  return {
    isUpdating,
    pendingRowId,
    updateNote,
  }
}
